import { useContext, useRef, useState } from 'react';
import merge from 'lodash/merge';
import { ForgePopover } from '@tylertech/forge-react';
import { SoQLType } from 'common/types/soql';
import { ParameterItemContext } from './ParameterItemContext';
import { ParameterItemControl } from './ParameterItemControl';
import { ParameterItemControlPopover } from './ParameterItemControlPopover';
import type { ParameterConfiguration, DataSourceCCVs } from 'common/types/reportFilters';
import type { FilterBarPendoIds } from '../types';

function ParameterItemCore({ pendoIds }: Pick<ParamItemProps, 'pendoIds'>) {
  const { parameter, onParameterUpdate, open, setOpen, toggleRef } = useContext(ParameterItemContext)!;
  const isBooleanParameter = parameter.dataType === SoQLType.SoQLBooleanT;

  const onUpdate = (parameterOverrideValue: string) => {
    const newParameter = merge({}, parameter, {
      overrideValue: parameterOverrideValue,
    });

    onParameterUpdate?.(newParameter);
    closeAndRefocus();
  };

  const closeAndRefocus = () => {
    setOpen(false);
    toggleRef.current?.focus();
  };

  return (
    <div className="filter-bar-filter parameter-item">
      <div
        className="filter-control-container"
        id={pendoIds?.filterParameterControlContainer}
        data-testid="filter-bar-parameter-control-container"
      >
        <ParameterItemControl onUpdate={onUpdate} />
        {!isBooleanParameter && (
          <ForgePopover
            anchor={`parameter-${parameter.paramIds[0].name}-${parameter.paramIds[0].datasetUid}-anchor`}
            on-forge-popover-toggle={(event: CustomEvent<{ newState: string }>) => {
              setOpen(event.detail.newState === 'open');
              if (event.detail.newState === 'closed') {
                if (!document.activeElement || document.activeElement === document.body) {
                  toggleRef.current?.focus();
                }
              }
            }}
            open={open}
            placement="bottom-start"
          >
            <div>{open && <ParameterItemControlPopover onUpdate={onUpdate} onClose={closeAndRefocus} />}</div>
          </ForgePopover>
        )}
      </div>
    </div>
  );
}

export type ParamItemProps = {
  parameter: ParameterConfiguration;
  clientContextVariables?: DataSourceCCVs;
  editMode?: boolean;
  onParameterUpdate?: (parameter: ParameterConfiguration) => void;
  pendoIds?: FilterBarPendoIds;
};

export function ParameterItem({ editMode = false, pendoIds, ...rest }: ParamItemProps) {
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLInputElement>(null);

  return (
    <ParameterItemContext.Provider
      value={{
        editMode,
        open,
        toggleRef,
        setOpen,
        ...rest,
      }}
    >
      <ParameterItemCore pendoIds={pendoIds} />
    </ParameterItemContext.Provider>
  );
}
